import {
  WooCommerceCartFee,
  WooCommerceCartInfo,
  WooCommerceCartItem,
  WooCommerceCartTotals
} from '../../interfaces/woo-commerce/api/cart/cart.interface';
import { WooCommerceCoupon } from '../../interfaces/woo-commerce/api/coupon/coupon-api.interface';
import { WooCommerceCheckoutResponse } from '../../interfaces/woo-commerce/api/checkout/checkout-api.interface';
import { WooCommerceProduct } from '../../interfaces/woo-commerce/api/products/product-api.interface';
import { BaseWooCommerceProductImage } from '../../interfaces/woo-commerce/api/products/base-product-api.interface';
import {
  WooCommerceRestaurant,
  WooCommerceRestaurantHoursValue
} from '../../interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import { WooCommerceShippingAddress } from '../../interfaces/woo-commerce/api/woo-commerce-api.interface';

const CURRENCY_CODE = 'USD';
const CURRENCY_SYMBOL = '$';
const CURRENCY_MINOR_UNIT = 2;

const toMinorString = (value: unknown): string => {
  if (value == null) {
    return '0';
  }

  const numeric =
    typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  if (Number.isNaN(numeric)) {
    return '0';
  }

  return String(Math.round(numeric * 100));
};

const withCurrency = () => ({
  currency_code: CURRENCY_CODE,
  currency_symbol: CURRENCY_SYMBOL,
  currency_minor_unit: CURRENCY_MINOR_UNIT,
  currency_decimal_separator: '.',
  currency_thousand_separator: ',',
  currency_prefix: '$',
  currency_suffix: ''
});

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value == null) {
    return undefined;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

export const mapHappyCouponsToWooCoupons = (
  coupons: any[] | undefined
): WooCommerceCoupon[] => {
  return (coupons || []).map((coupon) => ({
    coupon_id: toOptionalNumber(coupon?.coupon_id),
    code: coupon?.code || coupon?.id || '',
    type: coupon?.discount_type || 'fixed',
    totals: {
      ...withCurrency(),
      total_discount: toMinorString(coupon?.discount_amount ?? coupon?.discount ?? 0),
      total_discount_tax: '0'
    }
  }));
};

/**
 * Maps Happy API totals (Cart-Subs.md §9 format) to the WooCommerce totals shape.
 *
 * Cart-Subs totals are decimal strings (e.g. "45.00").
 * WooCommerce totals are minor-unit strings (e.g. "4500").
 *
 * Discount is the sum of coupon discounts + meal plan discount + gift card + points,
 * matching the pipeline: gift cards → points → dollar coupons → percentage coupons.
 */
export const mapHappyTotalsToWooTotals = (
  totals: any | undefined
): WooCommerceCartTotals => {
  const discountTotal = sumDecimalFields(
    totals?.discount_total,
    totals?.meal_plan_discount_total,
    totals?.gift_card_amount,
    totals?.points_discount
  );

  return {
    ...withCurrency(),
    total_items: toMinorString(totals?.subtotal ?? 0),
    total_items_tax: toMinorString(totals?.cart_tax ?? 0),
    total_fees: toMinorString(totals?.fees_total ?? totals?.tip_amount ?? 0),
    total_fees_tax: '0',
    total_discount: toMinorString(discountTotal),
    total_discount_tax: toMinorString(totals?.discount_tax ?? 0),
    total_shipping: toMinorString(totals?.shipping_total ?? 0),
    total_shipping_tax: toMinorString(totals?.shipping_tax ?? 0),
    points_redeemed: toOptionalNumber(totals?.points_redeemed) ?? 0,
    points_discount: toMinorString(totals?.points_discount ?? 0),
    total_price: toMinorString(totals?.total ?? 0),
    total_tax: toMinorString(totals?.total_tax ?? 0),
    tax_lines: totals?.tax_lines || totals?.tax_summaries || []
  };
};

const sumDecimalFields = (...values: unknown[]): number => {
  return values.reduce<number>((sum, v) => {
    if (v == null) return sum;
    const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
    return Number.isNaN(n) ? sum : sum + n;
  }, 0);
};

const parseDecimalAmount = (value: unknown): number => {
  if (value == null || value === '') {
    return 0;
  }
  const n =
    typeof value === 'string' ? Number.parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const tipAmountKeySuffix = (amount: number): string => {
  return amount.toFixed(2).replace('.', '-');
};

const rawFeesArrayHasTipEntry = (rawFees: any[] | undefined): boolean => {
  return (rawFees || []).some(
    (f) =>
      f?.fee_type === 'tip' ||
      String(f?.name || '')
        .trim()
        .toLowerCase() === 'tip'
  );
};

const mapHappyCartFeeToWoo = (fee: any): WooCommerceCartFee => {
  const amount = parseDecimalAmount(fee?.amount ?? fee?.total ?? 0);
  const isTip =
    fee?.fee_type === 'tip' ||
    String(fee?.name || '').trim().toLowerCase() === 'tip';
  const key =
    isTip && amount > 0
      ? `tip-amount-${tipAmountKeySuffix(amount)}`
      : String(fee?.id ?? fee?.name ?? 'fee');

  return {
    key,
    name: fee?.name || (isTip ? 'Tip' : 'Fee'),
    totals: {
      ...withCurrency(),
      total: toMinorString(amount),
      total_tax: '0',
      value: amount
    }
  };
};

/**
 * When the API puts the tip on `tip_amount` only and leaves `fees` empty,
 * checkout still needs a fee line (key `tip-amount-*`) for display and
 * for CartStateModel.driverFee.
 */
const appendTipFeeFromTotalsIfNeeded = (
  fees: WooCommerceCartFee[],
  normalized: any
): WooCommerceCartFee[] => {
  const tipDec = parseDecimalAmount(normalized?.tip_amount);
  if (tipDec <= 0) {
    return fees;
  }
  if (rawFeesArrayHasTipEntry(normalized?.fees)) {
    return fees;
  }
  return [
    ...fees,
    {
      key: `tip-amount-${tipAmountKeySuffix(tipDec)}`,
      name: 'Tip',
      totals: {
        ...withCurrency(),
        total: toMinorString(tipDec),
        total_tax: '0',
        value: tipDec
      }
    }
  ];
};

const isMeaningfulString = (value: unknown): boolean => {
  if (value == null) return false;
  const trimmed = String(value).trim();
  return trimmed !== '' && trimmed.toLowerCase() !== 'null';
};

/**
 * Maps the Happy cart item `addons` array into the WooCommerce
 * `item_data` shape consumed by cart UI components.
 *
 * Backend returns an entry per addon_field even when nothing was selected
 * (empty `name`, `value: null`). We skip those so the UI doesn't render
 * empty addon groups. For priced addons, we forward `price` directly so
 * components don't need to extract it from the display name.
 */
const mapHappyCartItemAddons = (item: any): any[] => {
  const rawAddons: any[] = Array.isArray(item?.addons)
    ? item.addons
    : Array.isArray(item?.item_data)
      ? item.item_data
      : [];

  return rawAddons
    .filter((addon: any) => {
      return (
        isMeaningfulString(addon?.name ?? addon?.title ?? addon?.label) ||
        isMeaningfulString(addon?.value ?? addon?.amount)
      );
    })
    .map((addon: any) => {
      const name = String(addon?.name ?? addon?.title ?? addon?.label ?? '').trim();
      const rawValue = addon?.value ?? addon?.amount ?? '';
      const value = rawValue == null ? '' : String(rawValue);
      const rawPrice = addon?.price;
      const numericPrice =
        rawPrice == null || rawPrice === ''
          ? 0
          : Number(String(rawPrice).replace(/[^0-9.\-]/g, ''));

      return {
        display: name || value,
        name,
        value,
        price: Number.isFinite(numericPrice) ? numericPrice : 0
      };
    });
};

const mapHappyItemToWooItem = (item: any): WooCommerceCartItem => {
  const priceMinor = toMinorString(
    item?.unit_price ?? item?.prices?.price ?? item?.price ?? 0
  );
  const lineSubtotalMinor = toMinorString(
    item?.subtotal ?? item?.line_total ?? item?.total ?? 0
  );
  const lineTotalMinor = toMinorString(
    item?.total ?? item?.line_total ?? 0
  );
  const lineTaxMinor = toMinorString(item?.total_tax ?? 0);
  const productId = Number(item?.product_id ?? item?.id ?? 0);
  const quantity = Number(item?.quantity ?? 0);
  const cartItemId = item?.cart_item_id ?? item?.id ?? item?.key ?? productId;

  const images: { id: number; src?: string; thumbnail: string; srcset: string; sizes: string; name: string; alt: string }[] = [];
  if (item?.main_image_url) {
    images.push({
      id: productId,
      src: item.main_image_url,
      thumbnail: item.thumbnail_url || item.main_image_url,
      srcset: '',
      sizes: '',
      name: item?.product_name || item?.name || '',
      alt: item?.product_name || item?.name || ''
    });
  }

  return {
    key: String(cartItemId),
    id: productId,
    type: 'line_item',
    quantity,
    name: item?.product_name || item?.name || '',
    short_description: item?.short_description || '',
    description: item?.description || '',
    sku: item?.sku || '',
    low_stock_remaining: item?.low_stock_remaining ?? null,
    backorders_allowed: false,
    show_backorder_badge: false,
    sold_individually: false,
    permalink: item?.slug || '',
    images,
    variation: [],
    prices: {
      ...withCurrency(),
      price: priceMinor,
      regular_price: priceMinor,
      sale_price: item?.is_on_sale ? priceMinor : priceMinor,
      price_range: null,
      raw_prices: {
        precision: 2,
        price: priceMinor,
        regular_price: priceMinor,
        sale_price: priceMinor
      }
    },
    totals: {
      ...withCurrency(),
      line_subtotal: lineSubtotalMinor,
      line_subtotal_tax: lineTaxMinor,
      line_total: lineTotalMinor,
      line_total_tax: lineTaxMinor
    },
    quantity_limits: {
      minimum: 1,
      maximum: 999,
      multiple_of: 1,
      editable: true
    },
    item_data: mapHappyCartItemAddons(item),
    catalog_visibility: 'visible',
    extensions: {}
  };
};

export const mapHappyCartToWooCart = (
  payload: any,
  totalsPayload?: any
): WooCommerceCartInfo => {
  const normalized = payload?.data || payload || {};
  const items = (normalized?.items || []).map(mapHappyItemToWooItem);
  const coupons = mapHappyCouponsToWooCoupons(normalized?.coupons);

  let fees: WooCommerceCartFee[] = (normalized?.fees || []).map(
    mapHappyCartFeeToWoo
  );
  fees = appendTipFeeFromTotalsIfNeeded(fees, normalized);

  const totals = mapHappyTotalsToWooTotals(
    totalsPayload?.data || totalsPayload || normalized?.totals || normalized
  );

  return {
    id: payload?.data?.id || 0,
    items,
    items_count: items.reduce(
      (acc: number, item: WooCommerceCartItem) => acc + item.quantity,
      0
    ),
    items_weight: 0,
    needs_payment: Number(totals.total_price) > 0,
    needs_shipping: true,
    totals,
    fees,
    coupons,
    shipping_rates: [],
    shipping_address: {
      first_name: normalized?.shipping_address?.first_name || '',
      last_name: normalized?.shipping_address?.last_name || '',
      email: normalized?.shipping_address?.email || '',
      company: '',
      address_1: normalized?.shipping_address?.address_1 || '',
      address_2: normalized?.shipping_address?.address_2 || '',
      city: normalized?.shipping_address?.city || '',
      state: normalized?.shipping_address?.state || '',
      postcode: normalized?.shipping_address?.postcode || '',
      country: normalized?.shipping_address?.country || normalized?.shipping_address?.country_code || '',
      phone: normalized?.shipping_address?.phone || ''
    },
    billing_address: {
      first_name: normalized?.billing_address?.first_name || '',
      last_name: normalized?.billing_address?.last_name || '',
      email: normalized?.billing_address?.email || '',
      company: '',
      address_1: normalized?.billing_address?.address_1 || '',
      address_2: normalized?.billing_address?.address_2 || '',
      city: normalized?.billing_address?.city || '',
      state: normalized?.billing_address?.state || '',
      postcode: normalized?.billing_address?.postcode || '',
      country: normalized?.billing_address?.country || normalized?.billing_address?.country_code || '',
      phone: normalized?.billing_address?.phone || ''
    },
    payment_requirements: [],
    has_calculated_shipping: true,
    cross_sells: [],
    errors: [],
    payment_methods: [],
    extensions: {},

    has_forced_subscription: !!normalized?.has_forced_subscription,
    has_subscription_option: !!normalized?.has_subscription_option,
    subscription_id: normalized?.subscription_id ?? null
  };
};

export const mapHappyProductToWooProduct = (
  product: any
): WooCommerceProduct => {
  const images: BaseWooCommerceProductImage[] = [];
  if (product.main_image?.url) {
    images.push({
      src: product.main_image.url,
      name: product.main_image.name || '',
      alt: product.name || '',
      sizes: {} as any
    });
  }
  if (Array.isArray(product.gallery)) {
    product.gallery.forEach((img: any) => {
      if (img?.url) {
        images.push({
          src: img.url,
          name: img.name || '',
          alt: product.name || '',
          sizes: {} as any
        });
      }
    });
  }

  return {
    ...product,
    type: product.type || 'simple',
    permalink: product.permalink || '',
    description: product.description || product.short_description || '',
    short_description: product.short_description || '',
    on_sale: product.is_on_sale ?? false,
    price_html: '',
    average_rating: product.average_rating || '0',
    images,
    clover_pos_images: [],
    categories: product.categories || [],
    tags: product.tags || [],
    attributes: product.attributes || [],
    variations: product.variations || [],
    has_options: false,
    sold_individually: product.item_limit_per_order === 1,
    product_addons: product.product_addons || [],
    global_acf: product.global_acf || ({} as any),
    acf: product.acf || {},
    macrosList: product.macrosList || [],
    has_variants: product.has_variants ?? false,
    has_available_variants: product.has_available_variants ?? false,
    variants: [...(product.variants || [])].sort(
      (a: any, b: any) =>
        (a.effective_price ?? a.regular_price ?? 0) -
        (b.effective_price ?? b.regular_price ?? 0)
    ),
    parent: 0,
    variation: '',
    prices: {
      ...withCurrency(),
      price: toMinorString(product.effective_price ?? product.regular_price ?? 0),
      regular_price: toMinorString(product.regular_price ?? 0),
      sale_price: toMinorString(
        product.sale_price ?? product.effective_price ?? product.regular_price ?? 0
      ),
      price_range: null
    },
    review_count: 0,
    is_purchasable: true,
    is_in_stock: product.stock_status === 'in_stock',
    is_on_backorder: false,
    low_stock_remaining: null,
    add_to_cart: {
      text: 'Add to cart',
      description: '',
      url: '',
      minimum: 1,
      maximum: product.item_limit_per_order || 999,
      multiple_of: 1
    },
    extensions: {}
  };
};

/**
 * Converts 24h "HH:MM:SS" or "HH:MM" to 12h "h:mm am/pm".
 * e.g. "08:00:00" → "8:00 am", "22:00:00" → "10:00 pm"
 */
const to12Hour = (time24: string): string => {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  const period = h >= 12 ? 'pm' : 'am';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m.toString().padStart(2, '0')} ${period}`;
};

const normalizeWeekday = (rawDay: unknown): string => {
  const fallback = '';
  if (typeof rawDay === 'number' && Number.isInteger(rawDay)) {
    const numericDayMap = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday'
    ];
    return numericDayMap[rawDay] || fallback;
  }

  if (typeof rawDay !== 'string') {
    return fallback;
  }

  const value = rawDay.trim().toLowerCase();
  const weekdayMap: Record<string, string> = {
    monday: 'Monday',
    mon: 'Monday',
    tuesday: 'Tuesday',
    tue: 'Tuesday',
    tues: 'Tuesday',
    wednesday: 'Wednesday',
    wed: 'Wednesday',
    thursday: 'Thursday',
    thu: 'Thursday',
    thur: 'Thursday',
    thurs: 'Thursday',
    friday: 'Friday',
    fri: 'Friday',
    saturday: 'Saturday',
    sat: 'Saturday',
    sunday: 'Sunday',
    sun: 'Sunday'
  };

  return weekdayMap[value] || fallback;
};

const isOpenSchedule = (hoursEntry: any): boolean => {
  const status = hoursEntry?.status;
  if (typeof status === 'string') {
    return status.toLowerCase() === 'open';
  }
  if (typeof status === 'boolean') {
    return status;
  }
  if (typeof hoursEntry?.is_opened === 'boolean') {
    return hoursEntry.is_opened;
  }
  if (typeof hoursEntry?.is_open === 'boolean') {
    return hoursEntry.is_open;
  }
  return false;
};

const extractWorkingHours = (store: any): any[] => {
  if (Array.isArray(store?.working_hours)) {
    return store.working_hours;
  }

  if (Array.isArray(store?.profile?.working_hours)) {
    return store.profile.working_hours;
  }

  if (Array.isArray(store?.profile_page?.working_hours)) {
    return store.profile_page.working_hours;
  }

  if (Array.isArray(store?.profile_pages)) {
    const profileWithHours = store.profile_pages.find((profile: any) =>
      Array.isArray(profile?.working_hours)
    );
    if (profileWithHours) {
      return profileWithHours.working_hours;
    }
  }

  return [];
};

const mapHappyWorkingHours = (
  hours: any[]
): WooCommerceRestaurantHoursValue[] => {
  const weekdayOrder = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const mapped = (hours || [])
    .map((wh: any) => {
      const dayName = normalizeWeekday(
        wh?.day ?? wh?.weekday ?? wh?.week_day ?? wh?.day_name
      );
      const opened = isOpenSchedule(wh);
      const startTimeRaw = wh?.start_at ?? wh?.start_time ?? wh?.open_at;
      const endTimeRaw = wh?.end_at ?? wh?.close_time ?? wh?.close_at;

      return {
        acf_fc_layout: 'row',
        is_opened: opened,
        weekday: dayName,
        start_time: opened ? to12Hour(startTimeRaw || '') : undefined,
        close_time: opened ? to12Hour(endTimeRaw || '') : undefined,
        closed_message: opened ? '' : 'Closed'
      };
    })
    .filter((entry: WooCommerceRestaurantHoursValue) => Boolean(entry.weekday));

  return mapped.sort(
    (a: any, b: any) =>
      weekdayOrder.indexOf(a.weekday) - weekdayOrder.indexOf(b.weekday)
  );
};

export const mapHappyStoreInfoToWooRestaurant = (
  store: any
): WooCommerceRestaurant => {
  const logoUrl =
    typeof store.logo === 'string'
      ? store.logo
      : store.logo?.url || '';

  const iconUrl =
    typeof store.icon === 'string'
      ? store.icon
      : store.icon?.url || '';

  const coverImageUrl =
    typeof store.cover_image === 'string'
      ? store.cover_image
      : store.cover_image?.url || '';

  const addr = store.address || {};
  const isAddrObject = typeof addr === 'object' && addr !== null;
  const addressString = isAddrObject
    ? [addr.address_1, addr.address_2, addr.city, addr.postcode]
        .filter(Boolean)
        .join(', ')
    : String(addr);

  return {
    name: store.name || '',
    'description ': store.tag_line || '',
    about_us: store.about_us || '',
    locale: '',
    'date_format ': '',
    'time_format ': '',
    timezone: store.timezone || '',
    start_of_week: '',
    'icon ': '',
    genre: store.genre || store.type || '',
    store_type: store.type || '',
    favicon: iconUrl
      ? { default: iconUrl, 'size-full': iconUrl, 'size-32': iconUrl, 'size-192': iconUrl, 'size-180': iconUrl, 'size-270': iconUrl }
      : {} as any,
    main_icon: { url: iconUrl || logoUrl, width: 0, height: 0, title: '', alt: '', sizes: '' },
    hero_image: { url: coverImageUrl, width: 0, height: 0, title: '', alt: '', sizes: '' },
    banners: [],
    logo: logoUrl ? { url: logoUrl, width: 0, height: 0 } : undefined,
    location: {
      name: store.name || '',
      pick_up_lead_time: String(
        store?.delivery_types?.pickup_estimated_time ?? 0
      ),
      delivery_lead_time: String(
        store?.delivery_types?.delivery_estimated_time ?? 0
      ),
      pickup_enabled: store?.delivery_types?.pickup_enabled ?? true,
      delivery_enabled: store?.delivery_types?.delivery_enabled ?? true,
      phone: { label: 'Phone', value: store.phone || '', stipped: '' },
      email: { label: 'Email', value: store.email || '', stipped: '' },
      address: { label: 'Address', value: addressString, stipped: '' },
      hours: {
        label: 'Working Hours',
        value: mapHappyWorkingHours(extractWorkingHours(store)),
        use_extended: false,
        value_txt: ''
      }
    },
    social: {
      name: '',
      networks: (store.social_links || []).map((link: any) => ({
        acf_fc_layout: 'row',
        hide_title: false,
        label: link.label || link.platform || '',
        url: link.url || '',
        icon: link.platform || '',
        icon_hovered: '',
        icon_alt: link.platform || '',
        icon_alt_hovered: ''
      }))
    },
    store: {
      address_1: isAddrObject ? (addr.address_1 || '') : String(addr),
      address_2: isAddrObject ? (addr.address_2 || '') : '',
      city: isAddrObject ? (addr.city || '') : '',
      state: isAddrObject ? (addr.state?.name || addr.state_code || '') : '',
      postcode: isAddrObject ? (addr.postcode || '') : '',
      country: isAddrObject ? (addr.country?.name || addr.country_code || '') : '',
      currency: {
        label: store.currency || 'USD',
        code: store.currency || 'USD',
        symbol: '$'
      },
      pickup_locations: {},
      holidays: {}
    },
    theme_styles: store.theme_styles || {
      colors: { primary: '', secondary: '', title: '', extra: '' }
    },
    side_notices: []
  };
};

export interface HappyPickupLocationStoreContext {
  index: number;
  domain: string;
  name: string;
  tenant: string;
  storeApiKey: string;
}

export const mapHappyPickupLocationsToWooShippingAddresses = (
  pickupLocations: any[],
  storeContext?: HappyPickupLocationStoreContext
): WooCommerceShippingAddress[] => {
  return (pickupLocations || []).map((loc: any) => {
    const pickupLocationId = String(loc?.id || '');
    const locationName = loc?.name || '';
    const storeName = storeContext?.name || '';
    const businessName = storeName || locationName;

    const address1 = loc?.address?.address_1 || '';
    const address2 = loc?.address?.address_2 || '';
    const city = loc?.address?.city || '';
    const state = loc?.address?.state_code || loc?.address?.state || '';
    const postcode = loc?.address?.postcode || '';

    const addressLine = [
      [address1, address2].filter(Boolean).join(' '),
      city,
      [state, postcode].filter(Boolean).join(' ')
    ]
      .filter(Boolean)
      .join(', ');

    return {
      id: storeContext ? `${storeContext.index}:${pickupLocationId}` : pickupLocationId,
      pickup_location_id: pickupLocationId,
      storeIndex: storeContext?.index,
      storeDomain: storeContext?.domain,
      storeName,
      tenant: storeContext?.tenant,
      storeApiKey: storeContext?.storeApiKey,
      address_1: address1,
      address_2: address2,
      city,
      state,
      postcode,
      country: loc?.address?.country_code || loc?.address?.country || '',
      label: businessName && addressLine
        ? `${businessName} - ${addressLine}`
        : businessName || addressLine,
      datetime_settings: null as any
    };
  });
};

const mapAddress = (addr: any) => ({
  first_name: addr?.first_name || '',
  last_name: addr?.last_name || '',
  email: addr?.email || '',
  company: addr?.company || '',
  address_1: addr?.address_1 || '',
  address_2: addr?.address_2 || '',
  city: addr?.city || '',
  state: addr?.state || addr?.state_code || '',
  postcode: addr?.postcode || '',
  country: addr?.country || addr?.country_code || '',
  phone: addr?.phone || ''
});

export const mapHappyCheckoutToWooCheckout = (
  payload: any
): WooCommerceCheckoutResponse => {
  const normalized = payload?.data || payload || {};
  const order = normalized?.order || {};
  const payment = normalized?.payment || {};
  const orderTotals = order?.totals || {};

  const shippingLine = order?.shipping_line;
  const shippingLines = Array.isArray(order?.shipping_lines)
    ? order.shipping_lines
    : shippingLine
      ? [{
          id: Number(shippingLine.id || 0),
          method_title: String(shippingLine.method_title || ''),
          method_id: String(shippingLine.method_id || shippingLine.method_title || ''),
          instance_id: '',
          total: String(shippingLine.total ?? 0),
          total_tax: String(shippingLine.total_tax ?? 0),
          taxes: []
        }]
      : [];

  const tipAmount = orderTotals?.tip_amount ?? 0;
  const feeLines = Array.isArray(order?.fee_lines)
    ? order.fee_lines
    : Number(tipAmount) !== 0
      ? [{ total: String(tipAmount) }]
      : [];

  const totalTax = orderTotals?.total_tax ?? order?.total_tax ?? 0;
  const taxLines = Array.isArray(order?.tax_lines)
    ? order.tax_lines
    : Number(totalTax) !== 0
      ? [{ tax_total: String(totalTax) }]
      : [];

  const customerNote =
    order?.notes?.customer_note ?? order?.customer_note ?? '';

  const products = mapCheckoutItems(order?.items || []);

  return {
    order_id: Number(order?.id || payload?.order_id || 0),
    status: order?.status || payment?.status || 'processing',
    order_key: String(order?.order_number || order?.id || ''),
    order_number: String(order?.order_number || ''),
    customer_note: String(customerNote || ''),
    customer_id: Number(order?.customer_id || 0),
    fulfillment_type: order?.fulfillment_type || undefined,
    billing_address: mapAddress(order?.billing_address),
    shipping_address: mapAddress(order?.shipping_address),
    payment_method: String(order?.payment_method || payment?.gateway || ''),
    payment_result: {
      payment_status: String(payment?.status || 'authorized'),
      payment_details: [
        {
          key: 'transaction_id',
          value: String(payment?.transaction_id || '')
        }
      ],
      redirect_url: String(payment?.redirect_url || '')
    },
    additional_fields: [],
    extensions: {},
    expected_delivery_date:
      order?.expected_delivery_date || order?.delivery_at || new Date().toISOString(),
    payment_method_title: String(
      order?.payment_method_title || order?.payment_method || ''
    ),
    products,
    refunds: [],
    totals: {
      discount_total: String(
        orderTotals?.discount_total ?? orderTotals?.coupon_amount ?? order?.discount_total ?? 0
      ),
      discount_tax: String(orderTotals?.discount_tax ?? order?.discount_tax ?? 0),
      fee_lines: feeLines,
      coupon_lines: order?.coupon_lines || [],
      shipping_total: String(orderTotals?.shipping_total ?? order?.shipping_total ?? 0),
      shipping_tax: String(orderTotals?.shipping_tax ?? order?.shipping_tax ?? 0),
      shipping_lines: shippingLines,
      cart_tax: String(orderTotals?.cart_tax ?? order?.cart_tax ?? 0),
      tax_lines: taxLines,
      total: String(orderTotals?.total ?? order?.total ?? 0),
      total_tax: String(totalTax)
    }
  };
};

const mapCheckoutItems = (items: any[]): WooCommerceCheckoutResponse['products'] => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item: any) => ({
    id: Number(item?.id || item?.product_id || 0),
    name: String(item?.name || ''),
    quantity: Number(item?.quantity || 0),
    subtotal: String(item?.subtotal ?? item?.total ?? 0),
    subtotal_tax: String(item?.subtotal_tax ?? item?.total_tax ?? 0),
    total: String(item?.total ?? item?.subtotal ?? 0),
    total_tax: String(item?.total_tax ?? item?.subtotal_tax ?? 0),
    item_meta: mapCheckoutItemMeta(item),
    variant_name: item?.variant_name ?? null,
    variant_options: item?.variant_options ?? null
  }));
};

const mapCheckoutItemMeta = (item: any): { label: string; value: string }[] => {
  if (Array.isArray(item?.item_meta)) {
    return item.item_meta;
  }
  if (!Array.isArray(item?.addons)) {
    return [];
  }
  return item.addons.map((addon: any) => ({
    label: String(addon?.name || addon?.title || ''),
    value: String(addon?.value ?? addon?.amount ?? '')
  }));
};
